<template>
  <div id="app" :class="['app-container', { 'is-iframe': isInIframe }]">
    <!-- Loading durante inicialização do iframe -->
    <div v-if="isIframeLoading" class="iframe-loading" role="status" aria-live="polite">
      <div class="load-vignette" aria-hidden="true"></div>
      <div class="load-orbs" aria-hidden="true">
        <span
          v-for="ball in loadBalls"
          :key="ball.n"
          class="orb"
          :style="orbStyle(ball)"
        >{{ padBall(ball.n) }}</span>
      </div>
      <div class="loading-content">
        <div class="load-stage">
          <div class="load-ring"></div>
          <div class="load-core">{{ padBall(featuredBall) }}</div>
        </div>
        <h2 class="load-title">Boa sorte</h2>
        <p class="loading-text">{{ loadingMessage }}</p>
        <div class="load-bar" :aria-valuenow="Math.round(loadingProgress)" aria-valuemin="0" aria-valuemax="100">
          <div class="load-bar-fill" :style="{ width: loadingProgress + '%' }"></div>
        </div>
        <span class="load-pct">{{ Math.round(loadingProgress) }}%</span>
      </div>
    </div>
    
    <!-- Conteúdo principal -->
    <template v-else>
      <router-view v-if="!unauthorizedError" />
      <Unauthorized v-else :error-code="unauthorizedErrorCode" />
    </template>
    
    <Toast />
  </div>
</template>

<script>
import Toast from './components/Toast.vue'
import Unauthorized from './views/Unauthorized.vue'
import { useAuthStore } from './stores/auth'
import { useClientStore } from './stores/client'
import { useTheme } from './composables/useTheme'
import { persistIframeSession, getIframeSessionParams, syncWalletFromSession, clearIframeSession } from './utils/wallet'
import { saveWidgetAction, isWidgetOpenMessage, readWidgetActionFromUrl, peekWidgetAction } from './utils/widgetDeepLink'

export default {
  name: 'App',
  components: {
    Toast,
    Unauthorized
  },
  data() {
    return {
      unauthorizedError: false,
      unauthorizedErrorCode: null,
      isInIframe: false,
      isIframeLoading: false,
      loadingMessage: 'Preparando o jogo...',
      loadingProgress: 8,
      featuredBall: 7,
      iframeReady: false,
      isRegistering: false,
      loadHintIndex: 0,
      loadProgressTimer: null,
      loadHintTimer: null,
      loadBallTimer: null,
      loadFinishTimer: null,
      loadFinishing: false,
      gameHints: [
        'Preparando o jogo...',
        'Esquentando as bolas...',
        'Montando o palpite...',
        'Sorteando a sorte...',
        'Quase na hora...'
      ],
      loadBalls: [
        { n: 7, x: 8, y: 18, d: 0 },
        { n: 13, x: 78, y: 12, d: 0.4 },
        { n: 22, x: 12, y: 72, d: 0.8 },
        { n: 4, x: 84, y: 68, d: 1.1 },
        { n: 18, x: 48, y: 8, d: 0.2 },
        { n: 25, x: 90, y: 38, d: 1.6 },
        { n: 9, x: 6, y: 46, d: 1.3 },
        { n: 11, x: 70, y: 82, d: 0.6 }
      ]
    }
  },
  mounted() {
    // Inicializar tema (dark como padrão)
    const { initTheme } = useTheme()
    initTheme()
    
    // Inicializar stores
    const authStore = useAuthStore()
    const clientStore = useClientStore()
    
    if (!authStore.initialized) {
      authStore.initialize()
    }
    if (!clientStore.initialized) {
      clientStore.initialize()
    }
    
    // Verificar se está em iframe e comunicar com parent
    if (window.self !== window.top) {
      this.isInIframe = true;
      document.body.classList.add('is-iframe');
      // Deep-link do widget (query no src do iframe) — gravar antes do bootstrap
      try {
        const fromUrl = readWidgetActionFromUrl()
        if (fromUrl) {
          saveWidgetAction(fromUrl)
          const u = new URL(window.location.href)
          u.searchParams.delete('widget')
          u.searchParams.delete('wpayload')
          u.searchParams.delete('wts')
          window.history.replaceState({}, '', u.toString())
        }
      } catch {
        // ignore
      }
      this.isIframeLoading = true;
      this.startGameLoading();
      this.setupIframeCommunication();
      this.setupPrizeNotification();
      this.setupBetCancelationCheck();
      this.setupBetStatusCheck();
      this.setupUnauthorizedHandler();
      this.initializeIframe();
    }
  },
  beforeUnmount() {
    this.stopGameLoading();
  },
  methods: {
    padBall(n) {
      return String(n).padStart(2, '0');
    },
    referrerHost() {
      try {
        if (!document.referrer) return '';
        return new URL(document.referrer).hostname || '';
      } catch {
        return '';
      }
    },
    orbStyle(ball) {
      return {
        left: ball.x + '%',
        top: ball.y + '%',
        animationDelay: ball.d + 's'
      };
    },
    startGameLoading() {
      this.loadFinishing = false;
      this.loadingProgress = 10;
      this.loadingMessage = this.gameHints[0];
      this.loadHintIndex = 0;
      this.featuredBall = this.loadBalls[0].n;
      this.stopGameLoading();

      this.loadProgressTimer = setInterval(() => {
        if (this.loadFinishing) return;
        if (this.loadingProgress >= 88) return;
        const step = this.loadingProgress < 40 ? 2.4 : this.loadingProgress < 70 ? 1.4 : 0.6;
        this.loadingProgress = Math.min(88, this.loadingProgress + step);
      }, 180);

      this.loadHintTimer = setInterval(() => {
        if (this.loadFinishing) return;
        this.loadHintIndex = (this.loadHintIndex + 1) % this.gameHints.length;
        this.loadingMessage = this.gameHints[this.loadHintIndex];
      }, 1600);

      this.loadBallTimer = setInterval(() => {
        this.featuredBall = 1 + Math.floor(Math.random() * 25);
      }, 420);
    },
    stopGameLoading() {
      if (this.loadProgressTimer) {
        clearInterval(this.loadProgressTimer);
        this.loadProgressTimer = null;
      }
      if (this.loadHintTimer) {
        clearInterval(this.loadHintTimer);
        this.loadHintTimer = null;
      }
      if (this.loadBallTimer) {
        clearInterval(this.loadBallTimer);
        this.loadBallTimer = null;
      }
      if (this.loadFinishTimer) {
        clearTimeout(this.loadFinishTimer);
        this.loadFinishTimer = null;
      }
    },
    setupIframeCommunication() {
      // Enviar mensagem para parent quando carregar
      window.parent.postMessage({
        type: 'iframe-loaded',
        source: 'ab-bet-iframe'
      }, '*');

      // Escutar mensagens do parent
      window.addEventListener('message', (event) => {
        if (!event.data) return;
        
        // Se tiver type, processar normalmente
        if (event.data.type) {
          this.handleParentMessage(event.data);
          return;
        }
        
        // Se tiver userData diretamente (sem type), tentar registrar
        if (event.data.userData) {
          this.tryRegisterUser(event.data.userData, event.data.regionId);
          return;
        }
        
        // Se tiver name/email diretamente (dados do usuário), criar userData
        if ((event.data.name || event.data.email) && !event.data.sellerId) {
          const userData = {
            name: event.data.name,
            username: event.data.username || event.data.email,
            email: event.data.email,
            password: event.data.password,
            phone: event.data.phone
          };
          this.tryRegisterUser(userData, event.data.regionId);
          return;
        }
        
        // Tratar mensagens sem type mas com dados relevantes de seller
        if (event.data.hasSellerData !== undefined || event.data.sellerId !== undefined || event.data.regionId) {
          this.handleSellerData(event.data);
        }
      });
    },
    handleParentMessage(data) {
      const authStore = useAuthStore()
      const clientStore = useClientStore()
      
      switch (data.type) {
        case 'set-seller':
          // Receber dados do vendedor do parent e fazer login automático
          this.handleSellerData(data);
          break;
        case 'set-region':
          // Receber região do parent
          if (data.regionId) {
            localStorage.setItem('regionId', data.regionId);
          }
          break;
        case 'set-api-credentials':
          // Receber API Key e Secret do parent para usar nos endpoints de integração
          if (data.apiKey && data.apiSecret) {
            localStorage.setItem('apiKey', data.apiKey);
            localStorage.setItem('apiSecret', data.apiSecret);
            
            // Disparar evento de credenciais recebidas
            window.dispatchEvent(new CustomEvent('api-credentials-received'));
            
            // Limpar erro de autorização se estava mostrando
            if (this.unauthorizedError) {
              this.unauthorizedError = false;
              this.unauthorizedErrorCode = null;
            }
            
            if (this.isIframeLoading && this.loadingProgress < 55) {
              this.loadingProgress = 55;
            }
          } else {
            // Credenciais inválidas ou vazias
            this.showUnauthorizedError('INVALID_CREDENTIALS', 'Credenciais de API inválidas ou vazias');
            this.isIframeLoading = false;
          }
          break;
        case 'wallet-updated':
          // Opcional: depósito em tempo real no site pai. Apostar não depende disto.
          if (data.walletBalance !== undefined || data.WalletBalance !== undefined) {
            const rawBalance = data.walletBalance ?? data.WalletBalance
            const newBalance = parseFloat(rawBalance || 0);
            const newLimit = data.walletLimit !== undefined && data.walletLimit !== null
              ? parseFloat(data.walletLimit || 0)
              : (data.WalletLimit !== undefined && data.WalletLimit !== null
                ? parseFloat(data.WalletLimit || 0)
                : null);

            if (clientStore.client) {
              clientStore.client.walletBalance = newBalance;
              if (newLimit !== null) {
                clientStore.client.walletLimit = newLimit;
              }
              localStorage.setItem('client', JSON.stringify(clientStore.client));

              const aggregator = this.hasAggregatorSession() || localStorage.getItem('seamlessWallet') === '1'
              if (aggregator) {
                window.dispatchEvent(new CustomEvent('wallet-api-synced', {
                  detail: { walletBalance: newBalance },
                }))
              } else {
                this.syncWalletToApi(clientStore.client.id, newBalance, newLimit)
                  .finally(() => {
                    window.dispatchEvent(new CustomEvent('wallet-api-synced', {
                      detail: { walletBalance: newBalance },
                    }))
                  })
              }
            } else {
              window.dispatchEvent(new CustomEvent('wallet-api-synced', {
                detail: { walletBalance: newBalance },
              }))
            }

            window.dispatchEvent(new CustomEvent('wallet-updated', {
              detail: { walletBalance: newBalance },
            }));
            console.log('💰 Saldo atualizado pelo parceiro (parent):', newBalance);

            if (this.isIframeLoading && clientStore.client && clientStore.client.walletBalance !== undefined) {
              setTimeout(() => {
                this.finishIframeLoading();
              }, 300);
            }
          }
          break;
        case 'widget-open':
          // Widget externo (home do operador) pediu abrir aposta rápida / próximo sorteio
          if (isWidgetOpenMessage(data)) {
            saveWidgetAction(data)
            try {
              window.parent.postMessage({
                type: 'widget-open-ack',
                source: 'ab-bet-iframe',
                at: data.at || Date.now(),
              }, '*')
            } catch {
              // ignore
            }
            window.dispatchEvent(new CustomEvent('ralph-widget-open', { detail: data }))
            if (this.$router && this.$route?.name !== 'Home') {
              this.$router.push({ name: 'Home' }).catch(() => {})
            }
          }
          break;
        case 'request-wallet-sync':
          // Parent está solicitando o saldo atual do iframe
          if (clientStore.client && clientStore.client.walletBalance !== undefined) {
            window.parent.postMessage({
              type: 'wallet-sync-response',
              walletBalance: clientStore.client.walletBalance,
              sellerId: clientStore.client.id
            }, '*');
          }
          break;
        case 'request-pending-prizes':
          // Parent está solicitando prêmios pendentes
          this.checkPendingPrizes();
          break;
        case 'register-user':
          // Parent está solicitando registro de novo usuário
          if (data.userData) {
            this.tryRegisterUser(data.userData, data.regionId);
          }
          break;
        case 'login-user':
          // Parent está solicitando login de usuário
          if (data.userData) {
            this.tryLoginUser(data.userData);
          }
          break;
        case 'set-theme': {
          const theme = data.theme === 'dark' || data.theme === 'light' ? data.theme : null
          if (theme) {
            useTheme().applyTheme(theme, { notify: false })
          }
          break
        }
      }
    },
    
    async checkPendingPrizes() {
      const clientStore = useClientStore();
      
      if (!clientStore.client || !clientStore.clientId) {
        return;
      }
      
      // Verificar se está em iframe
      if (window.self === window.top) {
        return; // Não é iframe, não precisa notificar
      }
      
      try {
        // Importar http configurado
        const { http } = await import('./services/config');
        
        // Obter credenciais de API
        const apiKey = localStorage.getItem('apiKey');
        const apiSecret = localStorage.getItem('apiSecret');
        
        if (!apiKey || !apiSecret) {
          return; // Sem credenciais, não pode buscar
        }
        
        // Buscar prêmios pendentes via API
        const response = await http.get(
          `api/v1/integration/GetPendingPrizes/${clientStore.clientId}`,
          {
            headers: {
              'X-Api-Key': apiKey,
              'X-Api-Secret': apiSecret
            }
          }
        );
        
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          const { notifyPrizeAvailable } = await import('./utils/iframeMessages')
          notifyPrizeAvailable(response.data)
        }
      } catch (error) {
        // Silenciar erro se não houver prêmios ou se a API não estiver disponível
        console.debug('Verificação de prêmios:', error.message);
      }
    },
    
    setupPrizeNotification() {
      // Verificar prêmios pendentes periodicamente (a cada 10 minutos)
    },
    
    setupBetCancelationCheck() {
      // Verificar apostas canceladas periodicamente quando em iframe
      if (window.self !== window.top) {
        const clientStore = useClientStore();
        
        // Verificar a cada 30 segundos se há apostas canceladas
        setInterval(async () => {
          if (clientStore.isAuthenticated && clientStore.client?.id) {
            try {
              const { default: apiService } = await import('./services/apiService');
              
              // Buscar apostas recentes do seller
              const sellerId = clientStore.client.id;
              const regionId = clientStore.regionId;
              
              if (!sellerId || !regionId) return;
              
              // Buscar apostas dos últimos 5 minutos para verificar cancelamentos
              const endDate = new Date();
              const startDate = new Date(endDate.getTime() - 5 * 60 * 1000); // 5 minutos atrás
              
              const response = await apiService.searchBetsBySeller(sellerId, regionId, startDate, endDate, 1);
              
              // Verificar se há apostas canceladas
              const bets = response?.data || response?.result || response || [];
              const canceledBets = Array.isArray(bets) ? bets.filter(bet => bet.canceled === true) : [];
              
              // Notificar o cliente sobre cada aposta cancelada
              canceledBets.forEach(bet => {
                // Verificar se já notificamos este cancelamento (usar localStorage)
                const notifiedKey = `bet-canceled-${bet.id}`;
                if (!localStorage.getItem(notifiedKey)) {
                  // Notificar o cliente
                  this.notifyBetCanceled(bet.id, sellerId, bet.cancelReason || null);
                  
                  // Marcar como notificado
                  localStorage.setItem(notifiedKey, 'true');
                  
                  // Limpar após 1 hora
                  setTimeout(() => {
                    localStorage.removeItem(notifiedKey);
                  }, 60 * 60 * 1000);
                }
              });
            } catch (error) {
              // Erro silencioso - não é crítico
              console.error('Erro ao verificar apostas canceladas:', error);
            }
          }
        }, 30000); // Verificar a cada 30 segundos
      }
    },
    
    // Método para notificar o cliente quando uma aposta for cancelada
    notifyBetCanceled(betId, sellerId, cancelReason = null) {
      if (window.self !== window.top) {
        // Enviar mensagem para o cliente quando em iframe
        window.parent.postMessage({
          type: 'bet-canceled',
          betId: betId,
          sellerId: sellerId,
          cancelReason: cancelReason,
          canceledAt: new Date().toISOString()
        }, '*');
      }
    },
    
    setupBetStatusCheck() {
      // Verificar status de apostas (ganhou/perdeu) periodicamente quando em iframe
      if (window.self !== window.top) {
        const clientStore = useClientStore();
        
        // Verificar a cada 30 segundos se há mudanças de status
        setInterval(async () => {
          if (clientStore.isAuthenticated && clientStore.client?.id) {
            try {
              const { default: apiService } = await import('./services/apiService');
              
              // Buscar apostas recentes do seller (últimas 24 horas)
              const sellerId = clientStore.client.id;
              const regionId = clientStore.regionId;
              
              if (!sellerId || !regionId) return;
              
              // Buscar apostas das últimas 24 horas para verificar status
              const endDate = new Date();
              const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000); // 24 horas atrás
              
              const response = await apiService.searchBetsBySeller(sellerId, regionId, startDate, endDate, 1);
              
              // Verificar se há apostas
              const bets = response?.data || response?.result || response || [];
              if (!Array.isArray(bets) || bets.length === 0) return;
              
              // Para cada aposta, verificar se o status mudou
              bets.forEach(bet => {
                // O SearchBetsReponse usa BetId (PascalCase), mas pode vir como betId (camelCase) ou id
                const betId = bet.BetId || bet.betId || bet.id || bet.Id;
                if (!betId) return;
                
                // Determinar status atual da aposta
                let currentStatus = 'pending';
                let prizeValue = 0;
                
                // Se a aposta foi cancelada, status é 'canceled'
                // O SearchBetsReponse usa Canceled (PascalCase), mas pode vir como canceled (camelCase)
                if (bet.Canceled === true || bet.canceled === true) {
                  currentStatus = 'canceled';
                } 
                // Se tem TotalAwarded > 0, ganhou
                // O SearchBetsReponse usa TotalAwarded (PascalCase), mas pode vir como totalAwarded (camelCase)
                const totalAwarded = parseFloat(bet.TotalAwarded || bet.totalAwarded || 0);
                if (totalAwarded > 0) {
                  currentStatus = 'won';
                  prizeValue = totalAwarded;
                } else {
                  // Verificar se o sorteio já aconteceu (comparar betDateTime com data atual)
                  // O SearchBetsReponse usa DateTime (PascalCase), mas pode vir como dateTime (camelCase)
                  const betDateTime = bet.DateTime ? new Date(bet.DateTime) : 
                                     (bet.dateTime ? new Date(bet.dateTime) : 
                                     (bet.BetDateTime ? new Date(bet.BetDateTime) : 
                                     (bet.betDateTime ? new Date(bet.betDateTime) : null)));
                  if (betDateTime && betDateTime < new Date()) {
                    // Sorteio já aconteceu e não tem prêmios = perdeu
                    currentStatus = 'lost';
                  }
                }
                
                // Buscar status anterior armazenado
                const statusKey = `bet-status-${betId}`;
                const previousStatus = localStorage.getItem(statusKey);
                
                // Se o status mudou, notificar o cliente
                if (previousStatus !== currentStatus) {
                  this.notifyBetStatusChanged(betId, sellerId, currentStatus, prizeValue, bet);
                  
                  localStorage.setItem(statusKey, currentStatus);

                  window.dispatchEvent(new CustomEvent('ralph-bet-status', {
                    detail: { betId, sellerId, status: currentStatus, prizeValue }
                  }));

                  if (currentStatus === 'won') {
                    import('./composables/usePendingPrizes').then(({ refreshPendingPrizes }) => {
                      refreshPendingPrizes();
                    });
                  }
                  
                  setTimeout(() => {
                    localStorage.removeItem(statusKey);
                  }, 7 * 24 * 60 * 60 * 1000);
                }
              });
            } catch (error) {
              // Erro silencioso - não é crítico
              console.error('Erro ao verificar status de apostas:', error);
            }
          }
        }, 30000); // Verificar a cada 30 segundos
      }
    },
    
    // Método para notificar o cliente quando o status de uma aposta mudar
    notifyBetStatusChanged(betId, sellerId, status, prizeValue = 0, betData = null) {
      if (window.self !== window.top) {
        // Enviar mensagem para o cliente quando em iframe
        window.parent.postMessage({
          type: 'bet-status-updated',
          betId: betId,
          sellerId: sellerId,
          status: status, // 'won', 'lost', 'pending', 'canceled'
          prizeValue: prizeValue,
          betData: betData, // Dados completos da aposta (opcional)
          updatedAt: new Date().toISOString()
        }, '*');
      }
    },
    
    hasAggregatorSession() {
      try {
        const { session } = getIframeSessionParams();
        return !!session;
      } catch {
        return false;
      }
    },

    setupUnauthorizedHandler() {
      const self = this;
      
      // Escutar eventos de erro de autorização
      window.addEventListener('unauthorized-error', (event) => {
        if (self.hasAggregatorSession() || localStorage.getItem('seamlessWallet') === '1') {
          return;
        }
        self.unauthorizedError = true;
        self.unauthorizedErrorCode = event.detail?.status || '401';
        
        // Notificar o parent sobre o erro
        window.parent.postMessage({
          type: 'unauthorized-error',
          status: event.detail?.status,
          message: event.detail?.message || 'Credenciais de API inválidas'
        }, '*');
      });
      
      this.checkApiCredentials();
    },
    
    checkApiCredentials() {
      // Agregador: 1 iframe com ?session= — não exige apiKey/apiSecret no JavaScript.
      if (this.hasAggregatorSession() || localStorage.getItem('seamlessWallet') === '1') {
        return;
      }

      const apiKey = localStorage.getItem('apiKey');
      const apiSecret = localStorage.getItem('apiSecret');
      
      if (!apiKey || !apiSecret) {
        window.parent.postMessage({
          type: 'request-api-credentials'
        }, '*');
        
        const checkTimeout = setTimeout(() => {
          const currentApiKey = localStorage.getItem('apiKey');
          const currentApiSecret = localStorage.getItem('apiSecret');
          
          if (this.hasAggregatorSession() || localStorage.getItem('seamlessWallet') === '1') {
            return;
          }
          if ((!currentApiKey || !currentApiSecret) && !this.unauthorizedError) {
            this.showUnauthorizedError('NO_CREDENTIALS', 'Credenciais de API não foram configuradas');
          }
        }, 5000);
        
        window.addEventListener('api-credentials-received', () => {
          clearTimeout(checkTimeout);
        }, { once: true });
      }
    },
    
    showUnauthorizedError(status, message) {
      this.unauthorizedError = true;
      this.unauthorizedErrorCode = status || '401';
      this.stopGameLoading();
      this.isIframeLoading = false;
      
      window.parent.postMessage({
        type: 'unauthorized-error',
        status: status,
        message: message || 'Credenciais de API inválidas'
      }, '*');
    },
    
    async handleSellerData(data) {
      const clientStore = useClientStore();
      
      // Atualizar mensagem de loading
      if (this.isIframeLoading && this.loadingProgress < 62) {
        this.loadingProgress = 62;
      }
      
      // Caso 1: Seller completo já enviado
      if (data.seller && data.seller.id) {
        clientStore.login(data.seller, data.token || 'token-placeholder');
        
        // Salvar região se fornecida
        if (data.regionId) {
          localStorage.setItem('regionId', data.regionId);
        }
        
        // Solicitar saldo atualizado após login
        this.requestWalletSync();
        return;
      }
      
      // Caso 2: Apenas sellerId fornecido - buscar seller via API
      if (data.sellerId) {
        try {
          const { default: apiService } = await import('./services/apiService');
          const seller = await apiService.getSellerById(data.sellerId);
          
          if (seller && seller.id) {
            clientStore.login(seller, data.token || 'token-placeholder');
            
            // Salvar região se fornecida
            if (data.regionId) {
              localStorage.setItem('regionId', data.regionId);
            }
            
            // Solicitar saldo atualizado após login
            this.requestWalletSync();
          } else {
            this.requestSellerFromParent(data.sellerId, data.regionId);
          }
        } catch (error) {
          // Se for erro de autorização, não solicitar novamente (já será tratado pelo interceptor)
          if (error.message && !error.message.includes('API Key')) {
            this.requestSellerFromParent(data.sellerId, data.regionId);
          }
        }
        return;
      }
      
      // Caso 3: hasSellerData: false ou sellerId: undefined - tentar registrar ou solicitar ao parent
      if (data.hasSellerData === false || data.sellerId === undefined || data.sellerId === null) {
        // Salvar região se fornecida (mesmo sem seller)
        if (data.regionId) {
          localStorage.setItem('regionId', data.regionId);
        }
        
        // Tentar registrar o usuário se tiver dados do usuário do site cliente
        if (data.userData) {
          this.tryRegisterUser(data.userData, data.regionId);
        } else {
          // Solicitar dados ao parent
          this.requestSellerFromParent(data.sellerId, data.regionId);
        }
      }
    },
    
    requestSellerFromParent(sellerId, regionId) {
      // Solicitar ao parent que busque e envie os dados do seller
      window.parent.postMessage({
        type: 'request-seller-data',
        sellerId: sellerId,
        regionId: regionId
      }, '*');
    },
    
    async tryRegisterUser(userData, regionId) {
      const clientStore = useClientStore();
      
      // 🔥 PROTEÇÃO: Verificar se já está registrando para evitar duplicação
      if (this.isRegistering) {
        console.log('⚠️ Registro já em andamento, ignorando tentativa duplicada');
        return;
      }
      
      // 🔥 PROTEÇÃO: Verificar se já está autenticado
      if (clientStore.isAuthenticated && clientStore.client?.id) {
        console.log('⚠️ Usuário já autenticado, ignorando registro');
        return;
      }
      
      this.isRegistering = true;
      
      try {
        const { default: apiService } = await import('./services/apiService');
        
        // Verificar se tem credenciais de API
        const apiKey = localStorage.getItem('apiKey');
        const apiSecret = localStorage.getItem('apiSecret');
        
        if (!apiKey || !apiSecret) {
          window.parent.postMessage({
            type: 'request-api-credentials'
          }, '*');
          this.isRegistering = false;
          return;
        }
        
        // Validar dados obrigatórios
        const name = userData.name || userData.username;
        const username = userData.username || userData.email;
        const password = userData.password;
        
        if (!name || !username) {
          this.isRegistering = false;
          throw new Error('Nome e username/email são obrigatórios');
        }
        
        // 🔥 PROTEÇÃO: Verificar se já existe um registro recente para este username
        const lastRegisterKey = `last-register-${username}`;
        const lastRegisterTime = localStorage.getItem(lastRegisterKey);
        if (lastRegisterTime) {
          const timeSinceLastRegister = Date.now() - parseInt(lastRegisterTime);
          if (timeSinceLastRegister < 5000) { // 5 segundos
            console.log('⚠️ Registro recente detectado, ignorando tentativa duplicada');
            this.isRegistering = false;
            return;
          }
        }
        
        // Marcar timestamp do registro
        localStorage.setItem(lastRegisterKey, Date.now().toString());
        
        let seller = await apiService.registerClient(
          name,
          username,
          password || 'temp-password-' + Date.now(),
          userData.phone || null
        );
        
        // Se seller for o objeto completo com success/seller, extrair o seller
        if (seller && seller.seller) {
          seller = seller.seller;
        } else if (seller && seller.Seller) {
          seller = seller.Seller;
        }
        
        // Verificar se agora temos um seller válido
        if (seller && (seller.id || seller.Id)) {
          // Normalizar propriedades (pode vir com Id ou id)
          const normalizedSeller = {
            id: seller.id || seller.Id,
            name: seller.name || seller.Name,
            login: seller.login || seller.Login,
            username: seller.username || seller.Login,
            regionId: seller.regionId || seller.RegionId,
            walletBalance: seller.walletBalance || seller.WalletBalance || 0,
            walletLimit: seller.walletLimit || seller.WalletLimit || 0,
            point: seller.point || seller.Point || 'WEB_CLIENTE',
            isActive: seller.isActive !== undefined ? seller.isActive : (seller.IsActive !== undefined ? seller.IsActive : true)
          };
          
          clientStore.login(normalizedSeller, 'token-placeholder');
          
          // Salvar região se fornecida
          if (regionId) {
            localStorage.setItem('regionId', regionId);
          }
          
          // Notificar o parent sobre o registro
          window.parent.postMessage({
            type: 'user-registered',
            seller: normalizedSeller,
            regionId: regionId || normalizedSeller.regionId
          }, '*');
          
          // Solicitar saldo atualizado após registro
          this.requestWalletSync();
        } else {
          throw new Error('Resposta do registro inválida: seller não encontrado');
        }
      } catch (error) {
        // Se o erro for que o usuário já existe, tentar fazer login
        if (error.message && (error.message.includes('já existe') || error.message.includes('already exists') || error.message.includes('duplicate'))) {
          this.tryLoginUser(userData, regionId);
        } else {
          // Notificar o parent sobre o erro
          window.parent.postMessage({
            type: 'user-registration-error',
            error: error.message
          }, '*');
        }
      } finally {
        // Liberar flag de registro
        this.isRegistering = false;
      }
    },
    
    async syncWalletToApi(sellerId, walletBalance, walletLimit) {
      try {
        const { default: apiService } = await import('./services/apiService');
        const result = await apiService.updateSellerWallet(sellerId, walletBalance, walletLimit);
        console.log('💰 Saldo sincronizado com a API:', walletBalance);
        console.log('💰 Resposta da API:', result);
      } catch (error) {
        // Logar erro para debug, mas não bloquear
        console.error('❌ Erro ao sincronizar saldo com a API:', error.message || error);
      }
    },
    
    async tryLoginUser(userData, regionId) {
      const clientStore = useClientStore();
      
      try {
        const { default: apiService } = await import('./services/apiService');
        
        // Verificar se tem credenciais de API
        const apiKey = localStorage.getItem('apiKey');
        const apiSecret = localStorage.getItem('apiSecret');
        
        if (!apiKey || !apiSecret) {
          window.parent.postMessage({
            type: 'request-api-credentials'
          }, '*');
          return;
        }
        
        // Tentar fazer login
        const seller = await apiService.loginClient(
          userData.username || userData.email,
          userData.password || ''
        );
        
        if (seller && seller.id) {
          clientStore.login(seller, 'token-placeholder');
          
          // Salvar região se fornecida
          if (regionId) {
            localStorage.setItem('regionId', regionId);
          }
          
          // Notificar o parent sobre o login
          window.parent.postMessage({
            type: 'user-logged-in',
            seller: seller,
            regionId: regionId
          }, '*');
          
          // Solicitar saldo atualizado após login
          this.requestWalletSync();
        }
      } catch (error) {
        window.parent.postMessage({
          type: 'user-login-error',
          error: error.message
        }, '*');
      }
    },
    
    async initializeIframe() {
      // 1. Session token do agregador (1 iframe, sem apiSecret no JS)
      // 2. Fallback postMessage: credenciais + set-seller (máx 5s / 10s)
      
      const clientStore = useClientStore();
      
      try {
        let urlSession = '';
        let urlClient = '';
        try {
          const params = new URLSearchParams(window.location.search);
          urlSession = params.get('session') || params.get('Session') || '';
          urlClient = params.get('client') || params.get('t') || params.get('apiKey') || '';
        } catch {
          // ignore
        }

        const stored = getIframeSessionParams();
        const session = (urlSession || stored.session || '').trim();
        const sessionFromUrl = !!urlSession;
        const clientHint = (urlClient || stored.client || this.referrerHost() || window.location.hostname || '').trim();

        if (session) {
          persistIframeSession(session, clientHint, false);
          if (this.loadingProgress < 28) this.loadingProgress = 28;
          try {
            const { default: apiService } = await import('./services/apiService');
            const boot = await apiService.bootstrapIframeSession(session, clientHint);
            const seller = boot?.seller || boot?.Seller;
            if (seller && (seller.id || seller.Id)) {
              const normalizedSeller = {
                id: seller.id || seller.Id,
                name: seller.name || seller.Name,
                login: seller.login || seller.Login,
                username: seller.username || seller.Login,
                regionId: seller.regionId || seller.RegionId,
                walletBalance: seller.walletBalance ?? seller.WalletBalance ?? 0,
                walletLimit: seller.walletLimit ?? seller.WalletLimit ?? 0,
                point: seller.point || seller.Point || 'WEB_CLIENTE',
                isActive: seller.isActive !== undefined ? seller.isActive : (seller.IsActive !== undefined ? seller.IsActive : true)
              };
              clientStore.login(normalizedSeller, 'token-placeholder');

              const tenant = boot.tenant || boot.Tenant;
              if (tenant) {
                apiService.setCachedTenant({
                  id: tenant.id || tenant.Id,
                  name: tenant.name || tenant.Name,
                  domain: tenant.domain || tenant.Domain,
                  logoUrl: tenant.logoUrl || tenant.LogoUrl,
                  prizeExpirationDays: tenant.prizeExpirationDays ?? tenant.PrizeExpirationDays ?? 7,
                  active: tenant.active ?? tenant.Active
                });
              }

              const seamless = boot.seamlessWallet ?? boot.SeamlessWallet;
              persistIframeSession(session, clientHint, !!seamless);

              this.unauthorizedError = false;
              this.unauthorizedErrorCode = null;
              this.finishIframeLoading();
              return;
            }

            // Sessão só no localStorage (expirada): limpa e tenta fluxo normal — evita tela preta.
            if (!sessionFromUrl) {
              clearIframeSession();
              console.warn('Sessão em cache inválida; seguindo sem session.');
            } else {
              this.showUnauthorizedError('INVALID_SESSION', 'Sessão inválida ou expirada');
              this.finishIframeLoading();
              return;
            }
          } catch (sessionError) {
            const extra = {
              status: sessionError?.response?.status || null,
              url: `${sessionError?.config?.baseURL || ''}${sessionError?.config?.url || ''}`,
              params: sessionError?.config?.params || null,
              data: sessionError?.response?.data || null,
              message: sessionError?.message || String(sessionError)
            };
            console.error('Bootstrap de sessão falhou', extra);
            if (!sessionFromUrl) {
              clearIframeSession();
            } else {
              this.showUnauthorizedError('INVALID_SESSION', 'Sessão inválida ou expirada');
              this.finishIframeLoading();
              return;
            }
          }
        }

        // Verificar se já tem credenciais
        let apiKey = localStorage.getItem('apiKey');
        let apiSecret = localStorage.getItem('apiSecret');
        
        if (!apiKey || !apiSecret) {
          if (this.loadingProgress < 36) this.loadingProgress = 36;
          // Aguardar até receber credenciais (máx 5 segundos)
          await new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 segundos (50 * 100ms)
            
            const checkCredentials = () => {
              apiKey = localStorage.getItem('apiKey');
              apiSecret = localStorage.getItem('apiSecret');
              if (apiKey && apiSecret) {
                resolve();
              } else if (attempts >= maxAttempts) {
                // Timeout: finalizar mesmo sem credenciais
                resolve();
              } else {
                attempts++;
                setTimeout(checkCredentials, 100);
              }
            };
            checkCredentials();
          });
        }
        
        // Verificar se já tem seller autenticado
        if (clientStore.isAuthenticated && clientStore.client) {
          if (this.loadingProgress < 72) this.loadingProgress = 72;
          // Solicitar saldo atualizado
          this.requestWalletSync();
          
          // Aguardar saldo (máx 5 segundos)
          await new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 segundos
            
            const checkWallet = () => {
              if (clientStore.client && clientStore.client.walletBalance !== undefined) {
                resolve();
              } else if (attempts >= maxAttempts) {
                // Timeout: finalizar mesmo sem saldo
                resolve();
              } else {
                attempts++;
                setTimeout(checkWallet, 100);
              }
            };
            checkWallet();
          });
        } else {
          // Aguardar autenticação (máx 10 segundos)
          this.loadingProgress = Math.max(this.loadingProgress, 48);
          await new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 100; // 10 segundos
            
            const checkAuth = () => {
              if (clientStore.isAuthenticated && clientStore.client) {
                this.loadingProgress = Math.max(this.loadingProgress, 72);
                this.requestWalletSync();
                
                // Aguardar saldo após autenticação (máx 5 segundos)
                let walletAttempts = 0;
                const maxWalletAttempts = 50;
                
                const checkWallet = () => {
                  if (clientStore.client && clientStore.client.walletBalance !== undefined) {
                    resolve();
                  } else if (walletAttempts >= maxWalletAttempts) {
                    // Timeout: finalizar mesmo sem saldo
                    resolve();
                  } else {
                    walletAttempts++;
                    setTimeout(checkWallet, 100);
                  }
                };
                setTimeout(checkWallet, 500);
              } else if (attempts >= maxAttempts) {
                // Timeout: finalizar mesmo sem autenticação
                resolve();
              } else {
                attempts++;
                setTimeout(checkAuth, 100);
              }
            };
            checkAuth();
          });
        }
      } catch (error) {
        console.error('Erro na inicialização do iframe:', error);
      } finally {
        // Finalizar loading
        this.finishIframeLoading();
      }
    },
    
    async requestWalletSync() {
      const { session } = getIframeSessionParams();
      if (session) {
        try {
          await syncWalletFromSession(useClientStore());
          return;
        } catch {
          // continua no postMessage opcional
        }
      }
      try {
        window.parent.postMessage({
          type: 'request-wallet-sync',
          source: 'ab-bet-iframe',
        }, '*');
      } catch {
        // ignore
      }
    },
    
    finishIframeLoading() {
      if (this.iframeReady && !this.isIframeLoading) {
        return;
      }

      if (this.unauthorizedError) {
        this.stopGameLoading();
        this.isIframeLoading = false;
        this.iframeReady = true;
        return;
      }

      if (this.loadFinishing) {
        return;
      }

      this.loadFinishing = true;
      this.loadingProgress = 100;
      this.loadingMessage = 'Tá valendo!';

      const reveal = () => {
        const clientStore = useClientStore()
        this.stopGameLoading();
        this.isIframeLoading = false;
        this.iframeReady = true;

        window.parent.postMessage({
          type: 'iframe-ready',
          source: 'ab-bet-iframe',
          sellerId: clientStore.clientId,
          theme: useTheme().getTheme()
        }, '*');

        this.$nextTick(() => {
          if (!this.$route || this.$route.path === '/bet' || this.$route.path === '/bet/') {
            this.$router.replace({ name: 'BetModality' }).catch(err => {
              console.error('Navigation error:', err)
            })
          }
          // Reenvia deep-link do widget após a Home montar (mensagem pode ter chegado no loading)
          try {
            const queued = peekWidgetAction()
            if (queued && (queued.action === 'quick-bet' || queued.action === 'upcoming')) {
              window.dispatchEvent(new CustomEvent('ralph-widget-open', { detail: queued }))
            }
          } catch {
            // ignore
          }
        })

        import('./composables/usePendingPrizes').then(({ startPendingPrizesPoll }) => {
          startPendingPrizesPoll()
        })
      };

      this.loadFinishTimer = setTimeout(reveal, 520);
    }
  }
}
</script>

<style>
.app-container {
  width: 100%;
  min-height: 100vh;
  background: var(--bg-secondary, #F0F4F8);
  color: var(--text-primary, #1A1A1A);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.app-container:has(.bet-flow-container) {
  height: 100dvh;
  min-height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-container.is-iframe {
  min-height: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.app-container.is-iframe > * {
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}

.app-container.is-iframe > router-view {
  flex: 1;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.app-container.is-iframe > .iframe-loading {
  flex: none;
  max-height: none;
  overflow: hidden;
}

.iframe-loading {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(ellipse at 50% 18%, rgba(255, 193, 7, 0.18), transparent 42%),
    radial-gradient(ellipse at 20% 80%, rgba(27, 111, 124, 0.32), transparent 50%),
    linear-gradient(165deg, #061318 0%, #0E2A32 42%, #124E57 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.load-vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, transparent 35%, rgba(0, 0, 0, 0.45) 100%);
  pointer-events: none;
}

.load-orbs {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orb {
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 0.04em;
  color: #1A1200;
  background: radial-gradient(circle at 32% 28%, #FFF176 0%, #FFD54F 42%, #FF8F00 100%);
  box-shadow:
    0 8px 18px rgba(0, 0, 0, 0.35),
    inset 0 -6px 10px rgba(255, 111, 0, 0.35),
    inset 0 5px 8px rgba(255, 255, 255, 0.55);
  animation: orb-float 3.4s ease-in-out infinite;
  opacity: 0.85;
}

.loading-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  width: min(340px, calc(100% - 48px));
}

.load-stage {
  position: relative;
  width: 118px;
  height: 118px;
  margin: 0 auto 22px;
}

.load-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.12);
  border-top-color: #7ECAD4;
  border-right-color: #1B6F7C;
  animation: spin 0.9s linear infinite;
  filter: drop-shadow(0 0 10px rgba(255, 213, 79, 0.45));
}

.load-core {
  position: absolute;
  inset: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: 800;
  color: #1A1200;
  background: radial-gradient(circle at 30% 26%, #FFF59D, #FFC107 55%, #FF6F00);
  box-shadow:
    0 10px 22px rgba(0, 0, 0, 0.35),
    inset 0 -8px 12px rgba(230, 81, 0, 0.35);
  animation: core-pulse 1.1s ease-in-out infinite;
}

.load-title {
  margin: 0 0 8px;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.4);
}

.loading-text {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 22px;
  min-height: 24px;
  opacity: 0.95;
  animation: hint-fade 1.6s ease-in-out infinite;
}

.load-bar {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  box-shadow: inset 0 1px 4px rgba(0, 0, 0, 0.35);
}

.load-bar-fill {
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #124E57 0%, #5EB3C1 50%, #1B6F7C 100%);
  background-size: 200% 100%;
  box-shadow: 0 0 16px rgba(255, 193, 7, 0.55);
  animation: bar-shine 1.2s linear infinite;
  transition: width 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.load-pct {
  display: inline-block;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  opacity: 0.8;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes core-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.07); }
}

@keyframes orb-float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-14px) scale(1.06); }
}

@keyframes bar-shine {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

@keyframes hint-fade {
  0%, 100% { opacity: 0.72; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .load-ring,
  .load-core,
  .orb,
  .load-bar-fill,
  .loading-text {
    animation: none;
  }
}
</style>

